import React, { ReactNode, useEffect } from "react";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { Pay, IPayFlowRoutes } from "@modules/PaymentProcessing/Flows/Pay/Pay";
import { useAvailablePaymentMethods } from "@modules/PaymentProcessing/hooks/useAvailablePaymentMethods";
import { Box } from "@UI/Components/Box/Box";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { CheckOrder } from "./CheckOrder/CheckOrder";
import { createFlow } from "@Libs/Flow/services/createFlow";
import { withFlow } from "@Libs/Flow/hocs/withFlow";
import { useFlow } from "@Libs/Flow/hooks/useFlow";
import { isEmpty } from "lodash-es";
import {
  PaymentMethodProviderCombination,
  PaymentMethodType,
} from "@modules/PaymentProcessing/types";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { ChoosePlanForCheckout } from "./ChoosePlanForCheckout";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";

export type ICheckoutFlowRoutes = "CHOOSE_PLAN" | IPayFlowRoutes;

export const CheckoutFlow = createFlow<
  PaymentMethodProviderCombination<PaymentMethodType> & {
    isPaymentConfirmed?: boolean;
  },
  {
    paymentFormMessage?: ReactNode;
    basketId?: string;
    relatedServiceId?: number;
    skipChoosePlan?: boolean;
  }
>({
  root: "CHECKOUT",
});

export const Checkout = withFlow(CheckoutFlow, () => {
  const {
    rootRoute,
    onSuccess,
    onLeave,
    onPageMount,
    state: { paymentFormMessage, basketId, relatedServiceId, skipChoosePlan },
  } = useFlow(CheckoutFlow);
  const isActivationRateEnabled = useFeatureFlag("new-activation-rate");
  const { serviceOffer, isLoading: isServiceOfferLoading } =
    useServiceOffer(relatedServiceId);
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser({
    includes: ["currentSubscription"],
  });
  const paramsBasketId = useSearchParams("resourceId") ?? basketId;
  const {
    currentBasket,
    isLoading: isCurrentBasketLoading,
    setCurrentBasketId,
    currentBasketId,
  } = useCurrentBasket();

  const {
    enabledPaymentMethods,
    allowedPaymentMethods,
    isLoading: isAvailablePaymentMethodsLoading,
  } = useAvailablePaymentMethods({
    resourceType: "basket",
    resourceAttributes: { id: currentBasketId, items: currentBasket?.items },
  });

  const isBasketFromParamsLoading =
    !!(paramsBasketId && currentBasketId !== paramsBasketId) ||
    !currentBasketId;

  useEffect(() => {
    if (paramsBasketId) {
      setCurrentBasketId(paramsBasketId);
    }
  }, [paramsBasketId]);

  useEffect(() => {
    if (
      !isActivationRateEnabled &&
      !isCurrentBasketLoading &&
      !paramsBasketId &&
      !isBasketFromParamsLoading &&
      currentBasketId &&
      isEmpty(currentBasket?.items)
    ) {
      onLeave();
    }
  }, [isCurrentBasketLoading, currentBasket?.items, paramsBasketId, isActivationRateEnabled, onLeave]);

  if (
    isCurrentBasketLoading ||
    isAvailablePaymentMethodsLoading ||
    isBasketFromParamsLoading ||
    isServiceOfferLoading ||
    (isActivationRateEnabled && isCurrentUserLoading)
  ) {
    return <LoadingScreen />;
  }

  const itemIsService = currentBasket?.items?.[0]?.itemType === "service";
  const listworkerIsPro =
    !!serviceOffer?.serviceProvider?.user?.listworkerProId;
  const nonProWarning = itemIsService && !listworkerIsPro;

  return (
    <Box>
      <Routes>
        <Route
          fragments="ROOT"
          element={
            isActivationRateEnabled && !currentUser?.currentSubscription && !skipChoosePlan ? (
              <ChoosePlanForCheckout />
            ) : (
              <CheckOrder allowedPaymentMethods={allowedPaymentMethods} />
            )
          }
          matchAny
        />
        <Route
          fragments={Pay.root}
          matchAny
          element={
            <Pay
              onSuccess={onSuccess}
              backRoute={rootRoute}
              onLeave={onLeave}
              onPageMount={onPageMount}
              state={{
                allowedPaymentMethods,
                enabledPaymentMethods,
                resourceId: currentBasketId,
                resourceType: "basket",
                relatedServiceId,
                paymentFormMessage,
                nonProWarning,
              }}
            />
          }
        />
      </Routes>
    </Box>
  );
});
